<template>
  <div class="signin position-relative">
    <span class="d-none modal-close fa fa-times position-absolute" @click="onClose"/>
    <div class="d-flex flex-column mt-3 mb-3 ml-4 mr-4">
      <div class="d-flex justify-content-center">
        <div class="d-flex flex-row align-items-center mb-3">
          <Logo/>
          <h1 class="ml-3 mr-1">{{ title }}</h1>
        </div>
      </div>
      <h2 class="modal-subtitle mx-auto mb-4" v-if="subtitle">{{ subtitle }}</h2>
      <input class="mb-2 mx-auto text-lowercase" v-model="username" placeholder="Email address" autocapitalize="off" inputmode="email">
      <input class="mb-2 mx-auto" v-model="password" placeholder="Password" type="password">
      <p class="validation-error mb-2" v-if="showError">{{errorMessage}}</p>
      <p class="validation-hint mb-2" v-if="showPasswordReset">Forgotten password? <a @click="onPasswordReset()">Reset it here</a>.</p>
      <p class="validation-hint mb-2" v-if="showError">
        <span>Drop us a line at </span>
        <a href="mailto:support@aftv.co.uk" target="_blank">support@aftv.co.uk</a>
        <span> if you need help</span>
      </p>
      <div class="d-flex justify-content-center">
        <button class="secondary" width="auto" v-on:click="onRegister" v-if="isRegistrationEnabled">Register</button>
        <button class="primary" width="auto" v-on:click="onSignIn">Sign In</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { spinnakerApi } from '../js/SpinnakerApi.js'
import Logo from '../components/Logo.vue'

const reasonRegistrationConfirmed = 'REGISTRATION_CONFIRMED'
const reasonContentBlocked = 'CONTENT_BLOCKED'
const reasonPasswordReset = 'PASSWORD_RESET'
const errMissingCredential = 'Please input valid username and password.'
const errLoginFailed = 'Failed to sign in. Please try again later.'
const errConfirmationPending = 'Please follow the activation link in your confirmation email.'
const errIncorrectPassword = 'Incorrect password. Please try again.'
const errUnknownUser = 'Email address not registered.'
const errOffline = 'Please check your internet connection and try again.'
const errCodeUnexpected = 0
const errCodeIncorrectPassword = 221
const errCodeConfirmationPending = 267
const errCodeUnknownUsername = 241

export default {
  components: {
    Logo
  },
  name: 'SignIn',
  props: ['reason'],
  computed: mapState({
    title: function () {
      switch (this.reason) {
        case reasonContentBlocked:
          if (this.isBetaBlocker()) {
            return 'AFTV Beta Testers only!'
          } else {
            return 'AFTV Members only!'
          }
        case reasonRegistrationConfirmed:
          return 'Account activated!'
        case reasonPasswordReset:
          return 'Password reset successfully!'
      }

      return 'Enter your sign-in details'
    },
    subtitle: function () {
      switch (this.reason) {
        case reasonContentBlocked:
          if (this.isBetaBlocker()) {
            return this.$store.state.isBetaOpen
              ? 'Join the beta for free.'
              : 'Sorry, beta registration is now closed.'
          } else {
            return 'Sign in or register for free.'
          }
        case reasonRegistrationConfirmed:
          // fall through...
        case reasonPasswordReset:
          return 'Please sign in to continue.'
      }

      return null
    },
    showPasswordReset: function () {
      return this.errorCode === null || this.errorCode === errCodeIncorrectPassword
    },
    isRegistrationEnabled: function () {
      if (this.$store.state.isBeta) {
        return this.$store.state.isBetaOpen
      }
      return true
    }
  }),
  data: function () {
    return {
      username: '',
      password: '',
      showError: false,
      errorCode: null,
      errorMessage: errMissingCredential
    }
  },
  methods: {
    onClose () {
      this.$modal.hide('signin-modal')
    },
    getUserName () {
      if (this.username) {
        return this.username.toLowerCase().trim()
      }
    },
    onRegister () {
      this.$router.replace('register')
      this.$modal.hide('signin-modal')
    },
    onPasswordReset () {
      this.$modal.hide('signin-modal')
      this.$store.commit('startPasswordReset', this.getUserName())
    },
    async onSignIn () {
      if (this.username === '' || this.password === '') {
        this.errorMessage = errMissingCredential
        this.showError = true
        return
      }

      if (navigator.onLine === false) {
        this.errorMessage = errOffline
        this.showError = true
        return
      }

      this.$store.commit('showLoading')

      let stage = 'signIn failed'
      try {
        await spinnakerApi.signInByUserCredential(this.getUserName(), this.password)

        this.$store.commit('isRegisteredUser', true)
        this.$modal.hide('signin-modal')
        this.$store.commit('hideLoading')

        if (this.reason === reasonRegistrationConfirmed || this.reason === reasonPasswordReset) {
          this.$store.commit('navigation/goToHome')
        }
      } catch (err) {
        this.signInFailed(stage, err)
      }
    },
    signInFailed (stage, err) {
      this.$store.commit('hideLoading')
      this.$store.commit('isRegisteredUser', false)

      this.errorMessage = errLoginFailed
      this.errorCode = errCodeUnexpected
      this.showError = true
      if (err && err.response && err.response.status === 400 &&
          err.response.data && err.response.data.errorCode) {
        this.errorCode = err.response.data.errorCode
        switch (this.errorCode) {
          case errCodeUnknownUsername:
            this.errorMessage = errUnknownUser
            break
          case errCodeIncorrectPassword:
            this.errorMessage = errIncorrectPassword
            break
          case errCodeConfirmationPending:
            this.errorMessage = errConfirmationPending
            break
        }
        this.errorMessage += ` [${err.response.data.errorCode}]`
      }
    },
    isBetaBlocker () {
      return this.$store.state.isBeta && !this.$store.state.isRegisteredUser
    }
  }
}
</script>

<style scoped></style>
